import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { screenshotAPI, designerAPI } from '../lib/api';
import { 
  Upload, 
  Image as ImageIcon, 
  Trash2, 
  FileImage,
  X,
  Calendar,
  User,
  Download,
  Eye,
  ZoomIn,
  ZoomOut,
  CheckSquare,
  Square,
  Package
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { formatDate, cn } from '../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { Toaster, toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Screenshots = () => {
  const { user, isArtist, isManager } = useAuth();
  const { isDark } = useTheme();
  const [screenshots, setScreenshots] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [selectedDesigner, setSelectedDesigner] = useState('all');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [description, setDescription] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const [zoom, setZoom] = useState(1);
  
  // Bulk selection state
  const [selectedScreenshots, setSelectedScreenshots] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const fetchScreenshots = useCallback(async () => {
    try {
      const designerId = selectedDesigner === 'all' ? undefined : selectedDesigner;
      const response = await screenshotAPI.getAll(isArtist() ? undefined : designerId);
      setScreenshots(response.data);
      // Clear selection when changing filter
      setSelectedScreenshots(new Set());
    } catch (error) {
      console.error('Error fetching screenshots:', error);
    } finally {
      setLoading(false);
    }
  }, [isArtist, selectedDesigner]);

  useEffect(() => {
    const fetchData = async () => {
      if (!isArtist()) {
        try {
          const designersRes = await designerAPI.getAllIncludingInactive();
          setDesigners(designersRes.data);
        } catch (error) {
          console.error('Error fetching designers:', error);
        }
      }
      fetchScreenshots();
    };
    fetchData();
  }, [isArtist, fetchScreenshots]);

  useEffect(() => {
    if (!isArtist()) {
      fetchScreenshots();
    }
  }, [selectedDesigner, isArtist, fetchScreenshots]);

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} is not an image`);
          continue;
        }

        await screenshotAPI.upload(file, description);
        toast.success(`${file.name} uploaded successfully`);
      }
      setDescription('');
      fetchScreenshots();
    } catch (error) {
      console.error('Error uploading:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload screenshot');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this screenshot?')) return;

    try {
      await screenshotAPI.delete(id);
      toast.success('Screenshot deleted');
      setSelectedScreenshots(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
      fetchScreenshots();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete screenshot');
    }
  };

  const handlePreview = (screenshot) => {
    setPreviewImage(screenshot);
    setZoom(1);
  };

  const handleDownload = (filename, originalFilename) => {
    const downloadUrl = `${BACKEND_URL}/api/screenshots/download/${filename}`;
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = originalFilename || filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Download started');
  };

  const getImageUrl = (filename) => {
    return `${BACKEND_URL}/api/screenshots/file/${filename}`;
  };

  // Bulk selection handlers
  const toggleSelection = (id) => {
    setSelectedScreenshots(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    if (selectedScreenshots.size === screenshots.length) {
      setSelectedScreenshots(new Set());
    } else {
      setSelectedScreenshots(new Set(screenshots.map(s => s.id)));
    }
  };

  const handleBulkDownload = async () => {
    if (selectedScreenshots.size === 0) {
      toast.error('No screenshots selected');
      return;
    }

    setDownloading(true);
    toast.info(`Downloading ${selectedScreenshots.size} screenshots...`);

    try {
      const selectedItems = screenshots.filter(s => selectedScreenshots.has(s.id));
      
      // Download each file with a small delay to prevent overwhelming the browser
      for (let i = 0; i < selectedItems.length; i++) {
        const screenshot = selectedItems[i];
        const downloadUrl = `${BACKEND_URL}/api/screenshots/download/${screenshot.filename}`;
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = screenshot.original_filename || screenshot.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Small delay between downloads
        if (i < selectedItems.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }
      
      toast.success(`Downloaded ${selectedScreenshots.size} screenshots`);
      setSelectedScreenshots(new Set());
      setIsSelectionMode(false);
    } catch (error) {
      console.error('Error during bulk download:', error);
      toast.error('Failed to download some screenshots');
    } finally {
      setDownloading(false);
    }
  };

  const cancelSelection = () => {
    setSelectedScreenshots(new Set());
    setIsSelectionMode(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="screenshots-page">
      <Toaster position="top-right" richColors />
      
      {/* Preview Modal */}
      <AnimatePresence>
        {previewImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setPreviewImage(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="relative max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setPreviewImage(null)}
                className="absolute -top-12 right-0 p-2 text-white hover:text-gray-300 transition-colors"
                data-testid="close-preview-btn"
              >
                <X className="w-8 h-8" />
              </button>

              <div className="bg-white dark:bg-slate-800 rounded-t-xl p-4 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-800 dark:text-white">
                    {previewImage.original_filename}
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {!isArtist() && previewImage.designer_name && (
                      <span className="mr-3">
                        <User className="w-3 h-3 inline mr-1" />
                        {previewImage.designer_name}
                      </span>
                    )}
                    <Calendar className="w-3 h-3 inline mr-1" />
                    {formatDate(previewImage.uploaded_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setZoom(z => Math.max(0.5, z - 0.25))}
                    data-testid="zoom-out-btn"
                  >
                    <ZoomOut className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-slate-600 dark:text-slate-300 w-12 text-center">
                    {Math.round(zoom * 100)}%
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setZoom(z => Math.min(3, z + 0.25))}
                    data-testid="zoom-in-btn"
                  >
                    <ZoomIn className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="default"
                    onClick={() => handleDownload(previewImage.filename, previewImage.original_filename)}
                    className="ml-2 bg-[#331062] hover:bg-[#2A0D52]"
                    data-testid="download-preview-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>

              <div className="bg-slate-100 dark:bg-slate-900 rounded-b-xl overflow-auto max-h-[70vh] flex items-center justify-center p-4">
                <img
                  src={getImageUrl(previewImage.filename)}
                  alt={previewImage.original_filename}
                  className="max-w-full transition-transform duration-200"
                  style={{ transform: `scale(${zoom})` }}
                  data-testid="preview-image"
                />
              </div>

              {previewImage.description && (
                <div className="bg-white dark:bg-slate-800 rounded-xl mt-2 p-4">
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    <strong>Description:</strong> {previewImage.description}
                  </p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className={cn(
            "text-2xl md:text-3xl font-bold font-outfit",
            isDark ? "text-white" : "text-slate-800"
          )}>
            Screenshots
          </h1>
          <p className={cn("mt-1", isDark ? "text-slate-400" : "text-slate-500")}>
            {isArtist() ? 'Upload your Teams approval screenshots' : 'View and download team screenshots'}
          </p>
        </div>
        
        {/* Filters and Actions for Managers */}
        {!isArtist() && (
          <div className="flex flex-wrap items-center gap-3">
            {/* Designer Filter */}
            <Select
              value={selectedDesigner}
              onValueChange={setSelectedDesigner}
            >
              <SelectTrigger className="w-48" data-testid="designer-filter-select">
                <SelectValue placeholder="All Designers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Designers</SelectItem>
                {designers.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Bulk Selection Controls */}
            {screenshots.length > 0 && (
              <>
                {isSelectionMode ? (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={selectAll}
                      data-testid="select-all-btn"
                    >
                      {selectedScreenshots.size === screenshots.length ? (
                        <>
                          <CheckSquare className="w-4 h-4 mr-2" />
                          Deselect All
                        </>
                      ) : (
                        <>
                          <Square className="w-4 h-4 mr-2" />
                          Select All ({screenshots.length})
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={handleBulkDownload}
                      disabled={selectedScreenshots.size === 0 || downloading}
                      className="bg-[#331062] hover:bg-[#2A0D52]"
                      data-testid="bulk-download-btn"
                    >
                      {downloading ? (
                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                      ) : (
                        <Package className="w-4 h-4 mr-2" />
                      )}
                      Download ({selectedScreenshots.size})
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={cancelSelection}
                      data-testid="cancel-selection-btn"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => setIsSelectionMode(true)}
                    data-testid="enable-selection-btn"
                  >
                    <CheckSquare className="w-4 h-4 mr-2" />
                    Select for Download
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Selection Info Bar */}
      {isSelectionMode && selectedScreenshots.size > 0 && (
        <div className={cn(
          "p-3 rounded-lg flex items-center justify-between",
          isDark ? "bg-[#331062]/30 border border-[#331062]" : "bg-purple-50 border border-purple-200"
        )}>
          <span className={cn("text-sm font-medium", isDark ? "text-purple-300" : "text-purple-700")}>
            {selectedScreenshots.size} screenshot{selectedScreenshots.size > 1 ? 's' : ''} selected
          </span>
          <Button
            size="sm"
            onClick={handleBulkDownload}
            disabled={downloading}
            className="bg-[#331062] hover:bg-[#2A0D52]"
          >
            <Download className="w-4 h-4 mr-2" />
            Download Selected
          </Button>
        </div>
      )}

      {/* Upload Zone (Artists Only) */}
      {isArtist() && (
        <Card className={isDark ? "bg-slate-800 border-slate-700" : ""}>
          <CardContent className="p-6">
            <div
              className={cn(
                "upload-zone",
                dragOver && "dragover",
                isDark && "bg-slate-700 border-slate-600"
              )}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => document.getElementById('file-input').click()}
            >
              <input
                id="file-input"
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handleFileUpload(Array.from(e.target.files))}
                data-testid="file-input"
              />
              {uploading ? (
                <div className="flex flex-col items-center">
                  <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full mb-3" />
                  <p className={isDark ? "text-slate-300" : "text-slate-600"}>Uploading...</p>
                </div>
              ) : (
                <>
                  <Upload className={cn("w-12 h-12 mx-auto mb-4", isDark ? "text-slate-400" : "text-slate-400")} />
                  <p className={cn("text-lg font-medium", isDark ? "text-slate-200" : "text-slate-700")}>
                    Drop your screenshots here
                  </p>
                  <p className={cn("text-sm mt-1", isDark ? "text-slate-400" : "text-slate-500")}>
                    or click to browse files
                  </p>
                  <p className={cn("text-xs mt-4", isDark ? "text-slate-500" : "text-slate-400")}>
                    Supported formats: PNG, JPG, JPEG
                  </p>
                </>
              )}
            </div>
            <div className="mt-4">
              <Input
                placeholder="Add a description (optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                data-testid="screenshot-description-input"
                className={isDark ? "bg-slate-700 border-slate-600 text-white" : ""}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Screenshots Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence>
          {screenshots.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <FileImage className={cn("w-12 h-12 mx-auto mb-4", isDark ? "text-slate-600" : "text-slate-300")} />
              <h3 className={cn("text-lg font-medium", isDark ? "text-white" : "text-slate-800")}>
                No screenshots yet
              </h3>
              <p className={isDark ? "text-slate-400" : "text-slate-500"}>
                {isArtist() ? 'Upload your first screenshot' : 'No screenshots from this designer'}
              </p>
            </div>
          ) : (
            screenshots.map((screenshot, index) => (
              <motion.div
                key={screenshot.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card 
                  className={cn(
                    "overflow-hidden card-hover relative",
                    isDark ? "bg-slate-800 border-slate-700" : "",
                    isSelectionMode && selectedScreenshots.has(screenshot.id) && "ring-2 ring-[#331062]"
                  )} 
                  data-testid={`screenshot-${screenshot.id}`}
                >
                  {/* Selection Checkbox */}
                  {isSelectionMode && (
                    <div 
                      className="absolute top-3 left-3 z-10"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelection(screenshot.id);
                      }}
                    >
                      <div className={cn(
                        "w-6 h-6 rounded border-2 flex items-center justify-center cursor-pointer transition-colors",
                        selectedScreenshots.has(screenshot.id) 
                          ? "bg-[#331062] border-[#331062] text-white" 
                          : "bg-white/90 border-slate-300 hover:border-[#331062]"
                      )}>
                        {selectedScreenshots.has(screenshot.id) && (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div 
                    className={cn(
                      "aspect-video bg-slate-100 dark:bg-slate-900 relative group",
                      isSelectionMode && "cursor-pointer"
                    )}
                    onClick={isSelectionMode ? () => toggleSelection(screenshot.id) : undefined}
                  >
                    <img
                      src={getImageUrl(screenshot.filename)}
                      alt={screenshot.original_filename}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect fill="%23f1f5f9" width="400" height="300"/><text x="50%" y="50%" fill="%2394a3b8" text-anchor="middle" dy=".3em" font-family="sans-serif">Image Not Found</text></svg>';
                      }}
                    />
                    
                    {/* Hover overlay with actions - only show when not in selection mode */}
                    {!isSelectionMode && (
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handlePreview(screenshot)}
                          data-testid={`preview-screenshot-${screenshot.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Preview
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleDownload(screenshot.filename, screenshot.original_filename)}
                          data-testid={`download-screenshot-${screenshot.id}`}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    )}

                    {/* Delete button - show when not in selection mode */}
                    {!isSelectionMode && (isArtist() || isManager()) && (
                      <button
                        onClick={() => handleDelete(screenshot.id)}
                        className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                        data-testid={`delete-screenshot-${screenshot.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <p className={cn(
                      "font-medium truncate",
                      isDark ? "text-white" : "text-slate-800"
                    )}>
                      {screenshot.original_filename}
                    </p>
                    {screenshot.description && (
                      <p className={cn(
                        "text-sm mt-1 line-clamp-2",
                        isDark ? "text-slate-400" : "text-slate-500"
                      )}>
                        {screenshot.description}
                      </p>
                    )}
                    <div className={cn(
                      "flex items-center gap-4 mt-3 text-xs",
                      isDark ? "text-slate-500" : "text-slate-400"
                    )}>
                      {!isArtist() && screenshot.designer_name && (
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {screenshot.designer_name}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(screenshot.uploaded_at)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Screenshots;
